import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { InputError } from "@/components/ui/inputError";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { t } from "i18next";
import {
	ChevronDownIcon,
	PenLineIcon,
	PlusIcon,
	TrashIcon,
} from "lucide-react";
import { useState } from "react";
import type { CreateAccountFormData } from "@/types/account/account";
import type { CreateGroupFormData } from "@/types/group/group";
import { CreateAccountState } from "@/hooks/accounts/useCreateAccountStore";
import { SearchCompanyInput } from "./SearchCompanyInput";

interface SingleAccountViewProps {
	store: CreateAccountState;
	account: CreateAccountFormData;
	handleChange: (account: CreateAccountFormData) => void;
	handleDelete: (accountID: string) => void;
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
}

export const SingleAccountView = ({
	store,
	account,
	handleChange,
	handleDelete,
	isOpen,
	onOpenChange,
}: SingleAccountViewProps) => {
	const [editingGroupId, setEditingGroupId] = useState<string | null>(null);

	const handleEditGroup = (groupId: string) => {
		setEditingGroupId(groupId);
	};

	const handleSaveGroup = (group: CreateGroupFormData) => {
		store.editGroup(group);
		setEditingGroupId(null);
	};

	return (
		<Collapsible
			open={isOpen}
			onOpenChange={onOpenChange}
			className="border rounded-lg bg-white dark:bg-[#1d1e1e] dark:border-black"
		>
			<CollapsibleTrigger asChild>
				<div className="flex justify-between items-center p-4 cursor-pointer">
					<h3 className="text-lg font-medium">
						{account.name || t("admin.create.form.unnamed")}
					</h3>
					<div className="flex gap-2">
						<ChevronDownIcon
							className={cn("h-5 w-5", isOpen && "rotate-180")}
						/>
					</div>
				</div>
			</CollapsibleTrigger>
			<CollapsibleContent>
				<div className="p-6 border-t flex flex-col gap-4">
					<div className="flex flex-col md:flex-row gap-8">
						<div className="flex flex-grow flex-col gap-6">
							<div className="grid lg:grid-cols-6 gap-4">
								<div className="flex flex-col gap-3 col-span-4">
									<Label>{t("admin.create.form.name")}</Label>
									<SearchCompanyInput
										value={account.name}
										placeholder={t("admin.create.form.name")}
										onChange={(value) => {
											let error: string | null = null;
											if (value.length < 1) {
												error = t("admin.create.form.error.name-min");
											}
											if (value.length > 60) {
												error = t("admin.create.form.error.name-max");
											}
											handleChange({
												...account,
												name: value.toUpperCase(),
												error: error,
											});
										}}
										onSelect={(value) => {
											handleChange({
												...account,
												name: value.company_name,
												orgnr: value.business_id,
												email: value.email ?? "",
												phone: value.phone ?? "",
												error: null,
											});
										}}
									/>
								</div>
								<div className="flex flex-col gap-3 col-span-2">
									<Label>{t("admin.create.form.orgnr")}</Label>
									<Input
										className="bg-soft-white dark:bg-black"
										placeholder={t("admin.create.form.orgnr")}
										value={account.orgnr}
										onChange={(e) => {
											let error: string | null = null;
											const value = e.target.value;
											if (value.length !== 11) {
												error = t("admin.create.form.error.orgnr-length");
											}
											const regex = /^(NO)?\d{9}|\d{11}$/;
											if (!regex.test(value)) {
												error = t("admin.create.form.error.orgnr-format");
											}
											handleChange({
												...account,
												orgnr: value,
												error: error,
											});
										}}
									/>
								</div>
								<div className="flex col-span-2 flex-col gap-3">
									<Label>{t("admin.create.form.contactname")}</Label>
									<Input
										className="bg-soft-white dark:bg-black"
										placeholder={t("admin.create.form.contactname")}
										value={account.contactPerson}
										onChange={(e) => {
											handleChange({
												...account,
												contactPerson: e.target.value,
											});
										}}
									/>
								</div>
								<div className="flex flex-col col-span-2 gap-3">
									<Label>{t("admin.create.form.email")}</Label>
									<Input
										className="bg-soft-white dark:bg-black"
										placeholder={t("admin.create.form.email")}
										value={account.email}
										onChange={(e) => {
											let error: string | null = null;
											const value = e.target.value;
											if (value.length > 0 && !value.includes("@")) {
												error = t("admin.create.form.error.email-format");
											}
											handleChange({
												...account,
												email: e.target.value,
												error: error,
											});
										}}
									/>
								</div>
								<div className="flex flex-col col-span-2 gap-3">
									<Label>{t("admin.create.form.phone")}</Label>
									<Input
										className="bg-soft-white dark:bg-black"
										placeholder={t("admin.create.form.phone")}
										value={account.phone}
										onChange={(e) => {
											let error: string | null = null;
											const value = e.target.value;
											if (value.length > 0 && !value.includes("+")) {
												error = t("admin.create.form.error.phone-prefix");
												return handleChange({
													...account,
													phone: e.target.value,
													error: error,
												});
											}
											if (
												isNaN(Number(value.split("+")[1]?.replace(/\s/g, "")))
											) {
												error = t("admin.create.form.error.phone-format");
											}
											handleChange({
												...account,
												phone: e.target.value,
												error: error,
											});
										}}
									/>
								</div>
							</div>
							<InputError>{account.error}</InputError>
							<div className="flex flex-col gap-4">
								<p className="font-medium">{t("admin.create.form.groups")}</p>
								{store.groups.filter((group) => group.accountID === account.id)
									.length < 1 && (
									<div className="flex justify-center items-center">
										<p>0 {t("admin.create.form.groups")}</p>
									</div>
								)}
								<div className="flex flex-col gap-2">
									{store.groups
										.filter((group) => group.accountID === account.id)
										.map((group) => {
											const isEditing = editingGroupId === group.id;
											return (
												<div key={group.id} className="flex flex-col gap-2">
													<div className="flex items-center gap-2">
														{isEditing ? (
															<Input
																className="bg-soft-white dark:bg-black"
																value={group.name}
																onFocus={(e) => {
																	e.target.select();
																}}
																onChange={(e) => {
																	const value = e.target.value;
																	let error: string | null = null;
																	if (value.length < 1) {
																		error = t(
																			"admin.create.form.error.group-name-min"
																		);
																	}
																	if (value.length > 40) {
																		error = t(
																			"admin.create.form.error.group-name-max"
																		);
																	}
																	store.editGroup({
																		...group,
																		name: e.target.value,
																		error: error,
																	});
																}}
																onBlur={() => handleSaveGroup(group)}
																autoFocus
															/>
														) : (
															<div className="flex items-center gap-2 flex-grow">
																<span className="flex h-10 w-full rounded-md border border-border px-3 py-2 text-base md:text-sm">
																	{group.name}
																</span>
																<Button
																	variant="secondary"
																	size="sm"
																	onClick={() => handleEditGroup(group.id)}
																>
																	<PenLineIcon className="h-4 w-4" />
																</Button>
															</div>
														)}
														<AlertDialog>
															<AlertDialogTrigger asChild>
																<Button variant="destructive">
																	<TrashIcon />
																</Button>
															</AlertDialogTrigger>
															<AlertDialogContent>
																<AlertDialogHeader>
																	<AlertDialogTitle>
																		{t("admin.dialog.delete-group.title")}
																	</AlertDialogTitle>
																	<AlertDialogDescription>
																		{t(
																			"admin.dialog.delete-group.description",
																			{ groupName: group.name }
																		)}
																	</AlertDialogDescription>
																</AlertDialogHeader>
																<AlertDialogFooter>
																	<AlertDialogCancel>
																		{t("admin.button.cancel")}
																	</AlertDialogCancel>
																	<AlertDialogAction
																		onClick={() => store.removeGroup(group.id)}
																		className="bg-destructive hover:bg-destructive/80"
																	>
																		{t("admin.button.delete")}
																	</AlertDialogAction>
																</AlertDialogFooter>
															</AlertDialogContent>
														</AlertDialog>
													</div>
													<InputError>{group.error}</InputError>
												</div>
											);
										})}
									<Button
										variant="secondary"
										className="w-fit mx-auto mt-4 min-w-1/4"
										onClick={() => store.addGroup(account.id)}
									>
										<PlusIcon />
										{t("admin.button.add-group")}
									</Button>
								</div>
							</div>
						</div>
						<div className="flex flex-grow flex-col gap-5">
							<div className="flex justify-end">
								<AlertDialog>
									<AlertDialogTrigger asChild>
										<Button variant="destructive">
											<TrashIcon /> {t("admin.button.delete")}{" "}
											{t("admin.table.caption")}
										</Button>
									</AlertDialogTrigger>
									<AlertDialogContent>
										<AlertDialogHeader>
											<AlertDialogTitle>
												{t("admin.dialog.delete-account.title")}
											</AlertDialogTitle>
											<AlertDialogDescription>
												{t("admin.dialog.delete-account.description", {
													accountName: account.name,
												})}
											</AlertDialogDescription>
										</AlertDialogHeader>
										<AlertDialogFooter>
											<AlertDialogCancel>
												{t("admin.button.cancel")}
											</AlertDialogCancel>
											<AlertDialogAction
												className="bg-poppy hover:bg-poppy-80"
												onClick={() => {
													handleDelete(account.id);
												}}
											>
												{t("admin.button.delete")}
											</AlertDialogAction>
										</AlertDialogFooter>
									</AlertDialogContent>
								</AlertDialog>
							</div>
						</div>
					</div>
				</div>
			</CollapsibleContent>
		</Collapsible>
	);
};
